import { useQuery } from '@apollo/client/react';
import { Badge, Group, Select, Stack, Table, Text, TextInput, Title } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconSearch } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { QueryState } from '../components/QueryState';
import { TablePager } from '../components/TablePager';
import type { Plan, UserRole } from '../gql/graphql';
import { AdminUsersDocument } from '../graphql/operations';
import { PLANS, USER_ROLES, useLabels } from '../labels';

const PAGE_SIZE = 20;

export function UsersPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const labels = useLabels();
  const [search, setSearch] = useState('');
  const [plan, setPlan] = useState<Plan | null>(null);
  const [role, setRole] = useState<UserRole | null>(null);
  const [page, setPage] = useState(1);

  // A query per keystroke would be one request per letter of an e-mail.
  const [debouncedSearch] = useDebouncedValue(search, 300);

  const { data, loading, error } = useQuery(AdminUsersDocument, {
    variables: {
      search: debouncedSearch.trim() || null,
      plan,
      role,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    },
    fetchPolicy: 'cache-and-network',
  });

  return (
    <Stack gap="md">
      <Title order={2}>{t('users.title')}</Title>

      <Group gap="sm" align="end">
        <TextInput
          label={t('users.search')}
          placeholder={t('users.searchPlaceholder')}
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(event) => {
            setSearch(event.currentTarget.value);
            setPage(1);
          }}
          w={280}
        />
        <Select
          label={t('users.plan')}
          placeholder={t('users.anyPlan')}
          clearable
          data={PLANS.map((value) => ({ value, label: labels.plan(value) }))}
          value={plan}
          onChange={(value) => {
            setPlan(value as Plan | null);
            setPage(1);
          }}
          w={160}
        />
        <Select
          label={t('users.role')}
          placeholder={t('users.anyRole')}
          clearable
          data={USER_ROLES.map((value) => ({ value, label: labels.role(value) }))}
          value={role}
          onChange={(value) => {
            setRole(value as UserRole | null);
            setPage(1);
          }}
          w={160}
        />
      </Group>

      <QueryState loading={loading && !data} error={error}>
        {data && (
          <>
            <Table.ScrollContainer minWidth={720}>
              <Table highlightOnHover striped>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('users.columns.email')}</Table.Th>
                    <Table.Th>{t('users.columns.provider')}</Table.Th>
                    <Table.Th>{t('users.columns.role')}</Table.Th>
                    <Table.Th>{t('users.columns.plan')}</Table.Th>
                    <Table.Th>{t('users.columns.locale')}</Table.Th>
                    <Table.Th>{t('users.columns.stories')}</Table.Th>
                    <Table.Th>{t('users.columns.createdAt')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.adminUsers.items.map((user) => (
                    <Table.Tr
                      key={user.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => void navigate(`/users/${user.id}`)}
                    >
                      <Table.Td>
                        {user.email ?? <Text c="dimmed">{t('common.empty')}</Text>}
                      </Table.Td>
                      <Table.Td>
                        {user.providers.map((provider) => labels.provider(provider)).join(', ')}
                      </Table.Td>
                      <Table.Td>{labels.role(user.role)}</Table.Td>
                      <Table.Td>
                        <Badge
                          variant="light"
                          color={user.plan === 'plus' ? 'violet' : 'gray'}
                          size="sm"
                        >
                          {labels.plan(user.plan)}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="default" size="sm">
                          {user.locale.toUpperCase()}
                        </Badge>
                      </Table.Td>
                      <Table.Td>{user.storiesCount}</Table.Td>
                      <Table.Td>{labels.dateTime(user.createdAt)}</Table.Td>
                    </Table.Tr>
                  ))}
                  {data.adminUsers.items.length === 0 && (
                    <Table.Tr>
                      <Table.Td colSpan={7}>
                        <Text c="dimmed" ta="center" py="md">
                          {t('common.nothingFound')}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>

            <TablePager
              total={data.adminUsers.total}
              limit={PAGE_SIZE}
              page={page}
              onChange={setPage}
            />
          </>
        )}
      </QueryState>
    </Stack>
  );
}
